import { canStartLoadingDetails, type DocumentListRow, useOrderListState } from "./useOrderListState";
import { useCallback, useRef } from "react";
import {
  filterProducts,
  type GetOrderListSearchResults,
  getOrderListSearchResults,
  transformFormData,
} from "../common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { type Subscription } from "rxjs";
import { fetchOrderDetails } from "./api/fetchOrderDetails/fetchOrderDetails";
import { ReportType } from "~/feature/cart/cart/components/CartPage/Buttons/DownloadReportButton/api/downloadCartReport";
import { downloadOrderHistoryReport } from "./api/downloadOrderHistoryReport";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import Spinner from "../../common/components/Spinner/Spinner";
import { DownloadClickLink } from "../../common/components/Link";
import { IncludesSurchargeDisclaimer } from "../../common/components/Messages/IncludesSurchargeDisclaimer";

import classes from "./OrderListPage.module.less";
import { OrderListTable } from "./components/OrderListTable/OrderListTable";
import { OrderListSearchBox, type OrderListSearchBoxData } from "./components/OrderListSearchBox/OrderListSearchBox";
import { SearchBoxVariant } from "./components/OrderListSearchBox/SearchBoxVariant";
import { useTranslation } from "react-i18next";
import { fetchServiceCases } from "../OrderDetailsPage/components/ServiceCases/api";
import { Permission, userHasPermission } from "~/utils/permissions";
import type { OrderData } from "~/OrdersQuotes/common/modelsV2";
import { isErrorEntry } from "~/OrdersQuotes/common/utils/util";
import { Alert } from "~/common/m3/Alert/Alert";

export const OrderListPage = () => {
  const { t } = useTranslation();

  const [state, dispatch, actions] = useOrderListState();
  const { loading, error, results: rows } = state;
  const subscriptionRef = useRef<Subscription | null>(null);
  const submittedFormData = useRef<GetOrderListSearchResults | null>(null);

  const isAllOpen = getIsAllOpen(state.results);
  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const onSubmitHandler = useCallback((formData: OrderListSearchBoxData) => {
    subscriptionRef.current?.unsubscribe();
    const transformedData = transformFormData(formData);
    submittedFormData.current = transformedData;
    dispatch(actions.beginLoading());
    subscriptionRef.current = getOrderListSearchResults(transformedData).subscribe({
      error: () => dispatch(actions.errorLoading()),
      next: (data) => dispatch(actions.addResultPage(data)),
      complete: () => dispatch(actions.finishLoading()),
    });
  }, []);

  const loadSingleDetail = useCallback(async (row: DocumentListRow<OrderData>) => {
    const soNumber = row.data.soNumber;
    let accountNumber, salesOrg;
    if (isFromDifferentAccOrSalesOrg(row)) {
      accountNumber = row.data.account?.code;
      salesOrg = row.data.account?.salesOrg;
    }
    try {
      let data;
      let serviceCases = {};
      if (userHasPermission(Permission.CAN_SEE_SERVICE_CASES)) {
        [data, serviceCases] = await Promise.all([
          fetchOrderDetails(soNumber, accountNumber, salesOrg),
          fetchServiceCases(soNumber),
        ]);
      } else {
        data = await fetchOrderDetails(soNumber, accountNumber, salesOrg);
      }
      data.products = filterProducts(data.products, submittedFormData.current);
      data.serviceCases = serviceCases;
      dispatch(actions.finishLoadingDetails(data));
    } catch {
      dispatch(actions.errorLoadingDetails(soNumber));
    }
  }, []);

  const toggleOpenCb = useCallback(
    (row: DocumentListRow<OrderData>) => {
      dispatch(actions.toggleOpen(row.data.soNumber));
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row);
      }
    },
    [loadSingleDetail],
  );

  const toggleOpenAllCb = useCallback(() => {
    const newState = !isAllOpen;
    rows.forEach((row) => {
      if (newState && canStartLoadingDetails(row) && !isErrorEntry(row)) {
        loadSingleDetail(row);
      }
    });
    dispatch(actions.toggleAllOpen());
  }, [isAllOpen, loadSingleDetail, rows]);

  const downloadReportCb = (fileType: ReportType) => {
    downloadOrderHistoryReport(rows, fileType);
  };

  const containsSurcharge = rows.some(rowHasSurcharge);

  const disableInteractions = state.loading || state.error || webservicesDisabled;
  const showRows = !error && rows.length > 0;

  const renderReportButton = (type: ReportType, dataTestIdStr: string, typeStr: string) => (
    <DownloadClickLink
      onClick={() => downloadReportCb(type)}
      data-test-id={`orderList-seatchBox-btn-${dataTestIdStr}`}
      disabled={disableInteractions}
    >
      {t(`downloadFile.type.${typeStr}`)}
    </DownloadClickLink>
  );

  return (
    <div>
      <PageTitle title={t("orderListPage.title")} />

      {loading && <Spinner className="mb-3" />}

      {showRows && (
        <h2 className={classes.total} data-test-id={"orderAmount"}>
          {t("orderListPage.orderAmount", { count: rows.length ?? 0 })}
        </h2>
      )}

      <OrderListSearchBox onSubmit={onSubmitHandler} variant={SearchBoxVariant.ORDER} />

      {!loading && rows.length > 0 && (
        <div className={classes.reportButtonContainer}>
          <span className="scha__order-list__download-title">{t("orderListPage.downloads.title")}</span>
          {renderReportButton(ReportType.CSV, "downloadCSV", "csv")}
          {renderReportButton(ReportType.XLSX, "downloadExcel", "excel")}
        </div>
      )}

      {showRows && <OrderListTable rows={rows} toggleOpen={toggleOpenCb} toggleOpenAll={toggleOpenAllCb} />}

      {<IncludesSurchargeDisclaimer hasSurcharge={containsSurcharge} className={"mt-30"} />}

      {!webservicesDisabled && !loading && error && <Alert severity="error">{t("orderListPage.error")}</Alert>}

      {!loading && !error && !rows.length && (
        <h2 className="scha__order-history__total my-0">{t("orderListPage.noResults")}</h2>
      )}
    </div>
  );
};

export const rowHasSurcharge = (row: DocumentListRow<OrderData>) => {
  const {
    data: { hasSurcharge },
  } = row;

  return hasSurcharge;
};

export const getIsAllOpen = (rows: DocumentListRow<OrderData>[]) => rows.length > 0 && rows.every((item) => item.open);

const isFromDifferentAccOrSalesOrg = (row: DocumentListRow<OrderData>) => {
  return (
    (row.data.account?.code && row.data.account?.code !== window.SCHA.user.selectedAccount.uid) ||
    (row.data.account?.salesOrg && row.data.account?.salesOrg !== window.SCHA.user.selectedAccount.salesOrg)
  );
};
